"use client";

import { X } from "lucide-react";
import { Button } from "@/components/shadcn/button";
import { useAtom } from "jotai";
import { genieContext, useGenieToolDefinitions } from "@/lib/genie";
import { genieDebugLandmarksAtom } from "@/store/genie-atoms";

export function GenieDebugInspector({ onClose }: { onClose: () => void }) {
  const [debugLandmarks, setDebugLandmarks] = useAtom(genieDebugLandmarksAtom);
  const toolDefinitions = useGenieToolDefinitions();

  return (
    <div
      className="fixed inset-0 z-300 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-background rounded-lg shadow-xl w-[900px] max-w-[90vw] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <span className="font-semibold text-sm">Genie Debug Inspector</span>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-7 w-7"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 flex flex-col divide-y divide-border">
          <div className="pb-4 flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer text-xs">
              <input
                type="checkbox"
                checked={debugLandmarks}
                onChange={(e) => setDebugLandmarks(e.target.checked)}
                className="rounded"
              />
              Show landmark outlines
            </label>
          </div>
          <details className="pb-4 pt-4" open>
            <summary className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 cursor-pointer select-none">
              Tools ({toolDefinitions.length})
            </summary>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {toolDefinitions.map((t) => (
                <span
                  key={t.name}
                  className="text-xs bg-muted border border-border rounded-md px-2 py-1 font-mono"
                >
                  {t.name}
                </span>
              ))}
            </div>
          </details>
          {genieContext.getSnapshot().map((entry) => (
            <details key={entry.key} className="py-4" open>
              <summary className="cursor-pointer select-none">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {entry.key}
                </span>
                <p className="text-xs text-muted-foreground mt-0.5 ml-4">
                  {entry.description}
                </p>
              </summary>
              <pre className="text-xs whitespace-pre-wrap break-all bg-muted border border-border rounded-lg p-3 font-mono max-h-[250px] overflow-y-auto mt-2">
                {typeof entry.value === "string"
                  ? entry.value
                  : JSON.stringify(entry.value, null, 2)}
              </pre>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
}
